import braintree, { PayPalCheckout } from 'braintree-web';
import React, { createContext, useContext, useEffect, useState } from 'react';
import useSWR from 'swr';

import apiRequest from '@/services/apiRequest';
import { getCoreApi } from '@/utility/getCoreApi';

interface PayPalSDKOptions {
  currency?: string;
  intent?: 'authorize' | 'capture' | 'sale' | 'tokenize';
}

interface IBraintreeProviderProps extends PayPalSDKOptions {
  components?: 'buttons' | 'messages' | 'buttons,messages';
  buyerCountry?: string;
}

interface IBraintreeProviderContext {
  initializingPayPalCheckout: boolean;
  failedToInitializePayPalCheckout: boolean;
  payPalCheckoutInstance?: PayPalCheckout;
  payPalSDKOptions: PayPalSDKOptions;
  deviceData?: string;
}

interface ICreateClientTokenResponse {
  data?: {
    createClientToken?: {
      clientToken?: string;
    };
  };
}

const BraintreeProviderContext = createContext<IBraintreeProviderContext>({
  initializingPayPalCheckout: true,
  failedToInitializePayPalCheckout: false,
  payPalSDKOptions: {},
});

export const usePayPalCheckoutData = () => {
  return useContext(BraintreeProviderContext);
};

export const BraintreeProvider: React.FC<React.PropsWithChildren<IBraintreeProviderProps>> = ({
  currency = 'USD',
  intent = 'authorize',
  components = 'buttons,messages',
  buyerCountry,
  children,
}) => {
  const [initializingPayPalCheckout, setInitializingPayPalCheckout] = useState(true);
  const [failedToInitializePayPalCheckout, setFailedToInitializePayPalCheckout] = useState(false);
  const [payPalCheckoutInstance, setPayPalCheckoutInstance] = useState<PayPalCheckout>();
  const [deviceData, setDeviceData] = useState<string>();

  const { data: clientTokenResult, error: clientTokenError } = useSWR(
    `${getCoreApi()}/paypal/create-client-token`,
    (url: string) =>
      apiRequest<ICreateClientTokenResponse>({ url, method: 'POST', data: {} }, true, true),
    {
      revalidateOnMount: true,
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );
  const authorization = clientTokenResult?.data?.data?.createClientToken?.clientToken;

  useEffect(() => {
    if (clientTokenError) {
      setInitializingPayPalCheckout(false);
      setFailedToInitializePayPalCheckout(true);
    }
  }, [clientTokenError]);

  useEffect(() => {
    if (!authorization) return;

    braintree.client
      .create({ authorization })
      .then(async client => {
        await braintree.dataCollector.create({ client }).then(dataCollector => {
          setDeviceData(dataCollector.deviceData);
        });

        return braintree.paypalCheckout.create({ client });
      })
      .then(payPalCheckout =>
        payPalCheckout.loadPayPalSDK({
          currency,
          intent,
          vault: true,
          components,
          ...(buyerCountry ? { 'buyer-country': buyerCountry } : {}),
        }),
      )
      .then(payPalCheckout => setPayPalCheckoutInstance(payPalCheckout))
      .catch(() => setFailedToInitializePayPalCheckout(true))
      .finally(() => setInitializingPayPalCheckout(false));
  }, [authorization, currency, intent, components, buyerCountry]);

  return (
    <BraintreeProviderContext.Provider
      value={{
        initializingPayPalCheckout,
        failedToInitializePayPalCheckout,
        payPalCheckoutInstance,
        payPalSDKOptions: {
          currency,
          intent,
        },
        deviceData,
      }}>
      {children}
    </BraintreeProviderContext.Provider>
  );
};
